import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Save, Edit3, Eye, Trash2, Plus, RefreshCw, Globe, FileText, Settings, Search, Filter, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

// Frontend Content Management System Component
const FrontendContentManagementSystem = () => {
  // State management
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPage, setFilterPage] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Editor state
  const [editorData, setEditorData] = useState({
    id: '',
    page_section: '',
    section_type: 'text',
    title: '',
    content: '',
    content_fr: '',
    metadata: {},
    is_published: true,
    display_order: 1
  });

  // Content structure definition
  const contentStructure = {
    homepage: {
      label: 'Page d\'Accueil',
      sections: [
        { key: 'hero_section', label: 'Section Héro', type: 'hero' },
        { key: 'featured_properties', label: 'Propriétés en Vedette', type: 'properties' },
        { key: 'market_intelligence', label: 'Intelligence du Marché', type: 'analytics' },
        { key: 'testimonials', label: 'Témoignages', type: 'testimonials' },
        { key: 'legal_services', label: 'Services Juridiques', type: 'services' },
        { key: 'commune_explorer', label: 'Explorateur de Communes', type: 'navigation' }
      ]
    },
    'legal-services': {
      label: 'Services Juridiques',
      sections: [
        { key: 'service_overview', label: 'Aperçu des Services', type: 'text' },
        { key: 'document_preparation', label: 'Préparation de Documents', type: 'services' },
        { key: 'verification_center', label: 'Centre de Vérification', type: 'services' },
        { key: 'legal_education', label: 'Éducation Juridique', type: 'educational' },
        { key: 'trust_indicators', label: 'Indicateurs de Confiance', type: 'trust' },
        { key: 'appointment_scheduler', label: 'Planificateur de Rendez-vous', type: 'booking' },
        { key: 'faq_section', label: 'FAQ', type: 'faq' }
      ]
    },
    'about-trust': {
      label: 'À Propos & Confiance',
      sections: [
        { key: 'hero_section', label: 'Section Héro', type: 'hero' },
        { key: 'mission_section', label: 'Notre Mission', type: 'text' },
        { key: 'team_section', label: 'Notre Équipe', type: 'team' },
        { key: 'trust_section', label: 'Indicateurs de Confiance', type: 'trust' },
        { key: 'testimonials', label: 'Témoignages', type: 'testimonials' },
        { key: 'partnerships', label: 'Partenariats', type: 'partnerships' },
        { key: 'contact_section', label: 'Contact', type: 'contact' }
      ]
    },
    'commune-explorer': {
      label: 'Explorateur de Communes',
      sections: [
        { key: 'commune_hero', label: 'Section Héro', type: 'hero' },
        { key: 'commune_selector', label: 'Sélecteur de Communes', type: 'selector' },
        { key: 'commune_overview', label: 'Aperçu de la Commune', type: 'overview' },
        { key: 'infrastructure', label: 'Infrastructure', type: 'infrastructure' },
        { key: 'lifestyle', label: 'Style de Vie', type: 'lifestyle' },
        { key: 'investment_insights', label: 'Perspectives d\'Investissement', type: 'analytics' },
        { key: 'property_showcase', label: 'Vitrine des Propriétés', type: 'properties' },
        { key: 'resident_reviews', label: 'Avis des Résidents', type: 'reviews' }
      ]
    }
  };

  // Load content on component mount
  useEffect(() => {
    loadContent();
  }, []);

  // Load all content from database
  const loadContent = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('frontend_content')
        ?.select('*')
        ?.order('page_section')
        ?.order('display_order');

      if (error) throw error;

      console.log('✅ Contenu frontend chargé:', data?.length || 0, 'éléments');
      setContentItems(data || []);

    } catch (error) {
      console.error('Error loading content:', error);
      setErrorMessage('Erreur lors du chargement du contenu');
      
      // Generate default content structure if table is empty
      if (error?.code === 'PGRST116' || (contentItems?.length === 0)) {
        console.log('🔄 Génération de la structure de contenu par défaut...');
        await generateDefaultContent();
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate default content structure
  const generateDefaultContent = async () => {
    try {
      const defaultContent = [];
      let orderIndex = 1;

      // Generate content for each page
      Object.entries(contentStructure)?.forEach(([pageKey, page]) => {
        page?.sections?.forEach((section) => {
          defaultContent?.push({
            page_section: `${pageKey}.${section?.key}`,
            section_type: section?.type,
            title: section?.label,
            content: getDefaultContent(section?.type),
            content_fr: getDefaultContentFr(section?.type),
            metadata: getDefaultMetadata(section?.type),
            is_published: true,
            display_order: orderIndex++
          });
        });
      });

      const { data, error } = await supabase
        ?.from('frontend_content')
        ?.insert(defaultContent)
        ?.select();

      if (error) throw error;

      console.log('✅ Structure de contenu par défaut générée:', data?.length || 0, 'éléments');
      setContentItems(data || []);

    } catch (error) {
      console.error('Error generating default content:', error);
      setErrorMessage('Erreur lors de la génération du contenu par défaut');
    }
  };

  // Get default content based on type
  const getDefaultContent = (type) => {
    const defaults = {
      hero: 'Welcome to our premium real estate platform',
      text: 'Default content text that can be edited',
      services: 'Our comprehensive services include...',
      testimonials: 'What our clients say about us...',
      team: 'Meet our professional team...',
      trust: 'Trust indicators and certifications...',
      analytics: 'Market insights and data...',
      properties: 'Featured properties showcase...',
      educational: 'Educational content and guides...',
      faq: 'Frequently asked questions...',
      contact: 'Contact information and form...'
    };
    return defaults?.[type] || 'Default content';
  };

  // Get default French content
  const getDefaultContentFr = (type) => {
    const defaults = {
      hero: 'Bienvenue sur notre plateforme immobilière premium',
      text: 'Contenu de texte par défaut qui peut être modifié',
      services: 'Nos services complets incluent...',
      testimonials: 'Ce que nos clients disent de nous...',
      team: 'Rencontrez notre équipe professionnelle...',
      trust: 'Indicateurs de confiance et certifications...',
      analytics: 'Perspectives et données du marché...',
      properties: 'Vitrine des propriétés en vedette...',
      educational: 'Contenu éducatif et guides...',
      faq: 'Questions fréquemment posées...',
      contact: 'Informations de contact et formulaire...'
    };
    return defaults?.[type] || 'Contenu par défaut';
  };

  // Get default metadata
  const getDefaultMetadata = (type) => {
    return {
      editable: true,
      version: '1.0',
      last_modified: new Date()?.toISOString(),
      content_type: type,
      seo_optimized: false
    };
  };

  // Handle content editing
  const handleEdit = (content) => {
    setSelectedContent(content);
    setEditorData({
      id: content?.id || '',
      page_section: content?.page_section || '',
      section_type: content?.section_type || 'text',
      title: content?.title || '',
      content: content?.content || '',
      content_fr: content?.content_fr || '',
      metadata: content?.metadata || {},
      is_published: content?.is_published ?? true,
      display_order: content?.display_order || 1
    });
    setShowEditor(true);
  };

  // Handle new content creation
  const handleCreate = () => {
    setSelectedContent(null);
    setEditorData({
      id: '',
      page_section: '',
      section_type: 'text',
      title: '',
      content: '',
      content_fr: '',
      metadata: { editable: true, version: '1.0' },
      is_published: true,
      display_order: 1
    });
    setShowEditor(true);
  };

  // Save content changes
  const handleSave = async () => {
    if (!editorData?.title?.trim() || !editorData?.content?.trim()) {
      setErrorMessage('Le titre et le contenu sont obligatoires');
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      const contentData = {
        page_section: editorData?.page_section,
        section_type: editorData?.section_type,
        title: editorData?.title?.trim(),
        content: editorData?.content?.trim(),
        content_fr: editorData?.content_fr?.trim() || editorData?.content?.trim(),
        metadata: {
          ...editorData?.metadata,
          last_modified: new Date()?.toISOString(),
          version: (parseFloat(editorData?.metadata?.version || '1.0') + 0.1)?.toFixed(1)
        },
        is_published: editorData?.is_published,
        display_order: parseInt(editorData?.display_order) || 1
      };

      let result;

      if (editorData?.id) {
        // Update existing content
        const { data, error } = await supabase
          ?.from('frontend_content')
          ?.update(contentData)
          ?.eq('id', editorData?.id)
          ?.select();

        if (error) throw error;
        result = data?.[0];
        console.log('✅ Contenu mis à jour:', result);

      } else {
        // Create new content
        const { data, error } = await supabase
          ?.from('frontend_content')
          ?.insert(contentData)
          ?.select();

        if (error) throw error;
        result = data?.[0];
        console.log('✅ Nouveau contenu créé:', result);
      }

      setSuccessMessage(`Contenu "${result?.title}" sauvegardé avec succès !`);
      setShowEditor(false);
      await loadContent();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);

    } catch (error) {
      console.error('Error saving content:', error);
      setErrorMessage(`Erreur lors de la sauvegarde: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete content
  const handleDelete = async (contentId, title) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${title}" ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        ?.from('frontend_content')
        ?.delete()
        ?.eq('id', contentId);

      if (error) throw error;

      console.log('✅ Contenu supprimé:', title);
      setSuccessMessage(`Contenu "${title}" supprimé avec succès !`);
      await loadContent();

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting content:', error);
      setErrorMessage(`Erreur lors de la suppression: ${error?.message}`);
    }
  };

  // Toggle publish status
  const handleTogglePublish = async (content) => {
    try {
      const { error } = await supabase
        ?.from('frontend_content')
        ?.update({ is_published: !content?.is_published })
        ?.eq('id', content?.id);

      if (error) throw error;

      console.log('✅ Statut de publication modifié:', content?.title);
      await loadContent();

    } catch (error) {
      console.error('Error toggling publish status:', error);
      setErrorMessage('Erreur lors de la modification du statut');
    }
  };

  // Filter content based on search and filters
  const filteredContent = contentItems?.filter(item => {
    const matchesSearch = !searchTerm || 
      item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.page_section?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesPage = filterPage === 'all' || item?.page_section?.startsWith(filterPage);
    const matchesType = filterType === 'all' || item?.section_type === filterType;

    return matchesSearch && matchesPage && matchesType;
  });

  // Get unique pages and types for filters
  const uniquePages = [...new Set(contentItems?.map(item => item?.page_section?.split('.')?.[0]))]?.filter(Boolean);
  const uniqueTypes = [...new Set(contentItems?.map(item => item?.section_type))]?.filter(Boolean);

  return (
    <>
      <Helmet>
        <title>Système de Gestion de Contenu Frontend - CMS</title>
        <meta name="description" content="Interface administrative pour gérer tout le contenu des pages frontend" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Système de Gestion de Contenu Frontend
                </h1>
                <p className="text-gray-600">
                  Gérez tout le contenu de vos pages frontend avec des outils d'édition avancés
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => loadContent()}
                  variant="outline"
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                
                <Button
                  onClick={() => handleCreate()}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Nouveau Contenu
                </Button>
              </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-800">{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{errorMessage}</span>
              </div>
            )}
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Éléments</p>
                  <p className="text-2xl font-bold text-gray-900">{contentItems?.length || 0}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Publié</p>
                  <p className="text-2xl font-bold text-green-600">
                    {contentItems?.filter(item => item?.is_published)?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <Globe className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Brouillons</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {contentItems?.filter(item => !item?.is_published)?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-full">
                  <Edit3 className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pages</p>
                  <p className="text-2xl font-bold text-purple-600">{uniquePages?.length || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Rechercher par titre, contenu ou section..."
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Select
                  value={filterPage}
                  onChange={(e) => setFilterPage(e?.target?.value)}
                  className="w-40"
                >
                  <option value="all">Toutes les pages</option>
                  {uniquePages?.map(page => (
                    <option key={page} value={page}>
                      {contentStructure?.[page]?.label || page}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                  className="w-32"
                >
                  <option value="all">Tous types</option>
                  {uniqueTypes?.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {/* Content Tree View */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Contenu Frontend ({filteredContent?.length || 0})
              </h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-500">Chargement du contenu...</p>
              </div>
            ) : filteredContent?.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredContent?.map(item => (
                  <div key={item?.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            {item?.section_type === 'hero' && <Globe className="h-4 w-4 text-blue-600" />}
                            {item?.section_type === 'text' && <FileText className="h-4 w-4 text-gray-600" />}
                            {item?.section_type === 'services' && <Settings className="h-4 w-4 text-green-600" />}
                            {item?.section_type === 'testimonials' && <Edit3 className="h-4 w-4 text-yellow-600" />}
                            {(!['hero', 'text', 'services', 'testimonials']?.includes(item?.section_type)) && (
                              <FileText className="h-4 w-4 text-gray-600" />
                            )}
                            
                            <h4 className="font-medium text-gray-900">{item?.title}</h4>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                              {item?.section_type}
                            </span>
                            
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item?.is_published 
                                ? 'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item?.is_published ? 'Publié' : 'Brouillon'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Section:</span> {item?.page_section}
                        </div>
                        
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {item?.content?.substring(0, 150)}
                          {item?.content?.length > 150 ? '...' : ''}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Ordre: {item?.display_order}</span>
                          <span>Version: {item?.metadata?.version || '1.0'}</span>
                          {item?.metadata?.last_modified && (
                            <span>Modifié: {new Date(item?.metadata?.last_modified)?.toLocaleDateString('fr-FR')}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => handleEdit(item)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          onClick={() => handleTogglePublish(item)}
                          variant="outline"
                          size="sm"
                          className={item?.is_published ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}
                        >
                          {item?.is_published ? <Eye className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                        </Button>
                        
                        <Button
                          onClick={() => handleDelete(item?.id, item?.title)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterPage !== 'all' || filterType !== 'all' ?'Aucun contenu ne correspond aux critères de recherche' :'Aucun contenu disponible'
                  }
                </p>
                {!searchTerm && filterPage === 'all' && filterType === 'all' && (
                  <Button
                    onClick={() => handleCreate()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Créer votre premier contenu
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Content Editor Modal */}
          {showEditor && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
              <div className="flex min-h-screen items-center justify-center p-4">
                <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
                  {/* Editor Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {selectedContent ? 'Modifier le Contenu' : 'Nouveau Contenu'}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {selectedContent 
                            ? `Modifiez "${selectedContent?.title}"`
                            : 'Créez un nouveau contenu pour votre frontend'
                          }
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowEditor(false)}
                        variant="outline"
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  {/* Editor Form */}
                  <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Informations de Base
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titre *
                          </label>
                          <Input
                            value={editorData?.title}
                            onChange={(e) => setEditorData(prev => ({ ...prev, title: e?.target?.value }))}
                            placeholder="Titre du contenu"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Section de Page *
                          </label>
                          <Input
                            value={editorData?.page_section}
                            onChange={(e) => setEditorData(prev => ({ ...prev, page_section: e?.target?.value }))}
                            placeholder="ex: homepage.hero_section"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type de Section
                          </label>
                          <Select
                            value={editorData?.section_type}
                            onChange={(e) => setEditorData(prev => ({ ...prev, section_type: e?.target?.value }))}
                          >
                            <option value="text">Texte</option>
                            <option value="hero">Héro</option>
                            <option value="services">Services</option>
                            <option value="testimonials">Témoignages</option>
                            <option value="team">Équipe</option>
                            <option value="trust">Confiance</option>
                            <option value="analytics">Analyses</option>
                            <option value="properties">Propriétés</option>
                            <option value="educational">Éducatif</option>
                            <option value="faq">FAQ</option>
                            <option value="contact">Contact</option>
                          </Select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ordre d'Affichage
                          </label>
                          <Input
                            type="number"
                            value={editorData?.display_order}
                            onChange={(e) => setEditorData(prev => ({ ...prev, display_order: e?.target?.value }))}
                            min="1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Contenu
                      </h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu Principal *
                        </label>
                        <textarea
                          value={editorData?.content}
                          onChange={(e) => setEditorData(prev => ({ ...prev, content: e?.target?.value }))}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Contenu principal en anglais..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu Français
                        </label>
                        <textarea
                          value={editorData?.content_fr}
                          onChange={(e) => setEditorData(prev => ({ ...prev, content_fr: e?.target?.value }))}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Traduction française du contenu..."
                        />
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                        Paramètres
                      </h3>
                      
                      <div className="flex items-center gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editorData?.is_published}
                            onChange={(e) => setEditorData(prev => ({ ...prev, is_published: e?.target?.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700">Publié</span>
                        </label>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -m-6 mt-6 rounded-b-lg">
                      <div className="flex justify-end gap-3">
                        <Button
                          onClick={() => setShowEditor(false)}
                          variant="outline"
                          disabled={saving}
                        >
                          Annuler
                        </Button>
                        
                        <Button
                          onClick={handleSave}
                          disabled={saving || !editorData?.title?.trim() || !editorData?.content?.trim()}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        >
                          {saving ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FrontendContentManagementSystem;